import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Headphones, TrendingUp, Database, Camera, Shirt, LineChart } from 'lucide-react';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import { Link } from 'react-router-dom';

/**
 * Public /about page.
 * Signals to Google the "product family" via structured data + Our Other Apps strip.
 */

const SISTER_APPS = [
  {
    name: 'Racing Form Guide',
    url: 'https://racingformguide.co.uk',
    tagline: 'Data-driven horse racing form guides',
    icon: TrendingUp,
    accent: 'from-emerald-500/20 to-emerald-500/5',
    testid: 'sister-racing',
  },
  {
    name: 'Raw Data',
    url: 'https://raw-data.uk',
    tagline: 'Secure enterprise data node',
    icon: Database,
    accent: 'from-blue-500/20 to-blue-500/5',
    testid: 'sister-raw-data',
  },
  {
    name: 'SnapDog',
    url: 'https://dogsnap.co.uk',
    tagline: 'AI-powered dog identification & care',
    icon: Camera,
    accent: 'from-purple-500/20 to-purple-500/5',
    testid: 'sister-snapdog',
  },
  {
    name: 'Raw Company Clothing',
    url: 'https://rawcompanyclothing.com',
    tagline: 'Streetwear from the Raw family',
    icon: Shirt,
    accent: 'from-rose-500/20 to-rose-500/5',
    testid: 'sister-clothing',
  },
  {
    name: 'Raw Co Trading',
    url: 'https://rawcotrading.com',
    tagline: 'Live gold trading signals',
    icon: LineChart,
    accent: 'from-[#FFB000]/25 to-[#FFB000]/5',
    testid: 'sister-trading',
  },
];

const About = () => {
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "RAW COMPANY TECH LIMITED",
    "url": "https://rawcompanytech.com",
    "identifier": {
      "@type": "PropertyValue",
      "name": "Company Number",
      "value": "17349121"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB",
      "addressRegion": "England & Wales"
    },
    "brand": {
      "@type": "Brand",
      "name": "Raw Mixes",
      "url": "https://rawmixes.uk"
    },
    "subOrganization": SISTER_APPS.map(a => ({
      "@type": "Organization",
      "name": a.name,
      "url": a.url,
    })),
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      {/* Header */}
      <header className="glass-top sticky top-0 z-40 px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="about-home-link">
          <Logo size="sm" />
        </Link>
        <Link
          to="/"
          className="btn-pill btn-outline text-sm"
          data-testid="about-back-btn"
        >
          Back to site
        </Link>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-12 md:py-20 w-full">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#FFB000] mb-4">
            About Raw Mixes
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-6xl leading-[0.95] tracking-tighter mb-6">
            Where the mix hits before the drop.
          </h1>
          <p className="text-[#A1A1AA] text-lg leading-relaxed max-w-2xl">
            Raw Mixes is the social home for DJs, producers and crate-diggers.
            Post your latest cuts, chase new sounds, and connect with the people
            pushing the scene forward.
          </p>
        </motion.div>

        {/* Company */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-16"
        >
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#A1A1AA] mb-4">
            Parent company
          </div>
          <div className="post-card p-6 md:p-8" itemScope itemType="https://schema.org/Organization">
            <h2 className="font-heading font-bold text-2xl text-white mb-2" itemProp="legalName">
              RAW COMPANY TECH LIMITED
            </h2>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Registered in England &amp; Wales. Company No.{' '}
              <span className="text-white font-bold" itemProp="identifier">17349121</span>.
            </p>
            <p className="text-white/80 text-sm leading-relaxed mb-5">
              Raw Mixes is an official product of RAW COMPANY TECH LIMITED — a small British
              tech studio building focused tools across music, data, trading and
              lifestyle. Everything under this brand is built, owned and operated by the
              same team.
            </p>
            <a
              href="https://rawcompanytech.com"
              target="_blank"
              rel="noopener noreferrer me"
              className="btn-pill btn-primary inline-flex items-center gap-2 text-sm"
              data-testid="about-visit-parent"
              itemProp="url"
            >
              Visit rawcompanytech.com <ExternalLink size={14} />
            </a>
          </div>
        </motion.section>

        {/* Our other apps */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16"
          aria-labelledby="our-other-apps"
        >
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#A1A1AA] mb-4">
            The product family
          </div>
          <h2 id="our-other-apps" className="font-heading font-black text-white text-3xl md:text-4xl tracking-tight mb-8">
            Our other apps
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SISTER_APPS.map(app => {
              const Icon = app.icon;
              return (
                <a
                  key={app.url}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="post-card p-5 flex items-center gap-4 group"
                  data-testid={app.testid}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${app.accent} border border-white/5 flex items-center justify-center flex-shrink-0`}>
                    <Icon size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-white text-lg group-hover:text-[#FFB000] transition-colors truncate">
                        {app.name}
                      </h3>
                      <ExternalLink size={12} className="text-[#A1A1AA] flex-shrink-0" />
                    </div>
                    <p className="text-[#A1A1AA] text-sm truncate">{app.tagline}</p>
                  </div>
                </a>
              );
            })}

            {/* Raw Mixes itself, marked current */}
            <div
              className="post-card p-5 flex items-center gap-4 border-[#FFB000]/40"
              data-testid="sister-rawmixes-current"
              aria-current="page"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFB000]/25 to-[#FFB000]/5 border border-[#FFB000]/30 flex items-center justify-center flex-shrink-0">
                <Headphones size={22} className="text-[#FFB000]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-[#FFB000] text-lg truncate">
                    Raw Mixes
                  </h3>
                  <span className="text-[10px] uppercase tracking-widest text-[#FFB000] bg-[#FFB000]/10 px-1.5 py-0.5 rounded">
                    You are here
                  </span>
                </div>
                <p className="text-[#A1A1AA] text-sm truncate">
                  Social home for DJs and producers
                </p>
              </div>
            </div>
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
